/**
 * Ops map fluency — camera-first scheduling Soft invent Soft invent Soft invent Soft paint wars.
 * Soft≠Live: never cuts road/satellite/building detail. Yields overlay *work* during gestures
 * so MapLibre owns the GPU for tile zoom; full ink resumes on idle (no permanent 降级).
 */

import { opsMapCameraBusy } from "./ops-map-device-pins.js";

/** @typedef {{ id: string, data: object }} DeferredSource */

/**
 * True when overlay RAF / GeoJSON churn must yield to camera + tiles.
 * @param {import("maplibre-gl").Map | null | undefined} map
 */
export function opsMapCamYield(map) {
  if (!map) return false;
  if (map.__opsCamYield) return true;
  return opsMapCameraBusy(map);
}

/**
 * Queue GeoJSON setData until camera idle Soft invent Soft invent Soft mid-zoom parse spikes.
 * @param {import("maplibre-gl").Map} map
 * @param {string} sourceId
 * @param {object} data
 */
export function opsMapDeferSourceData(map, sourceId, data) {
  if (!map || !sourceId || !data) return;
  if (!opsMapCamYield(map)) {
    try {
      const src = map.getSource(sourceId);
      if (src && typeof src.setData === "function") src.setData(data);
    } catch {
      /* */
    }
    return;
  }
  if (!map.__opsDeferredSrc) map.__opsDeferredSrc = new Map();
  map.__opsDeferredSrc.set(sourceId, data);
}

/**
 * Apply any deferred GeoJSON after gesture Soft invent Soft invent Soft invent Soft idle.
 * @param {import("maplibre-gl").Map} map
 */
export function opsMapFlushDeferredSources(map) {
  if (!map || !map.__opsDeferredSrc) return;
  const q = map.__opsDeferredSrc;
  map.__opsDeferredSrc = null;
  for (const [id, data] of q) {
    try {
      const src = map.getSource(id);
      if (src && typeof src.setData === "function") src.setData(data);
    } catch {
      /* */
    }
  }
}

/**
 * Engine knobs Soft invent Soft invent Soft invent Soft faster zoom without dropping LOD.
 * Higher tile cache + parallel fetches Soft invent Soft invent Soft invent Soft snappier overzoom.
 * @param {import("maplibre-gl").Map} map
 */
export function tuneOpsMapEngine(map) {
  if (!map) return;
  try {
    /* MapLibre reads these from options at construct; also poke runtime where present. */
    if (typeof map.setMaxTileCacheSize === "function") {
      map.setMaxTileCacheSize(1200);
    } else if (map.style && map.style._maxTileCacheSize != null) {
      map.style._maxTileCacheSize = 1200;
    }
  } catch {
    /* */
  }
  try {
    if (map.painter && map.painter.context) {
      /* Keep full antialias Soft invent Soft invent Soft invent Soft no visual 降级. */
    }
  } catch {
    /* */
  }
}

/**
 * Wire camera yield Soft invent Soft invent Soft invent Soft flush deferred sources.
 * @param {import("maplibre-gl").Map} map
 * @returns {() => void} unbind
 */
export function installOpsMapFluency(map) {
  if (!map || map.__opsFluencyBound) return () => {};
  map.__opsFluencyBound = true;
  map.__opsCamYield = false;
  let endTimer = 0;

  const markBusy = () => {
    map.__opsCamYield = true;
    if (endTimer) {
      clearTimeout(endTimer);
      endTimer = 0;
    }
  };
  const markIdle = () => {
    /* Coalesce zoomend/moveend Soft invent Soft invent Soft invent Soft one flush. */
    if (endTimer) clearTimeout(endTimer);
    endTimer = setTimeout(() => {
      endTimer = 0;
      map.__opsCamYield = false;
      opsMapFlushDeferredSources(map);
      try {
        if (typeof map.triggerRepaint === "function") map.triggerRepaint();
      } catch {
        /* */
      }
    }, 32);
  };

  map.on("movestart", markBusy);
  map.on("zoomstart", markBusy);
  map.on("rotatestart", markBusy);
  map.on("pitchstart", markBusy);
  map.on("moveend", markIdle);
  map.on("zoomend", markIdle);
  map.on("rotateend", markIdle);
  map.on("pitchend", markIdle);

  tuneOpsMapEngine(map);

  try {
    if (typeof window !== "undefined") {
      window.__opsMapFluency = {
        yield: () => opsMapCamYield(map),
        cache: 1200,
        parallel: 32,
        cancelPendingWhileZooming: true,
      };
    }
  } catch {
    /* */
  }

  return () => {
    if (endTimer) clearTimeout(endTimer);
    try {
      map.off("movestart", markBusy);
      map.off("zoomstart", markBusy);
      map.off("rotatestart", markBusy);
      map.off("pitchstart", markBusy);
      map.off("moveend", markIdle);
      map.off("zoomend", markIdle);
      map.off("rotateend", markIdle);
      map.off("pitchend", markIdle);
    } catch {
      /* */
    }
    map.__opsFluencyBound = false;
  };
}

/**
 * Map construct options Soft invent Soft invent Soft invent Soft fluency (call with spread).
 * Detail unchanged Soft invent Soft invent Soft invent Soft more cache/parallel Soft invent Soft invent Soft invent Soft less fade fight.
 */
export function opsMapFluencyConstructOpts() {
  return {
    fadeDuration: 80,
    maxTileCacheSize: 1200,
    maxParallelImageRequests: 32,
    cancelPendingTileRequestsWhileZooming: true,
    refreshExpiredTiles: true,
    collectResourceTiming: false,
    localIdeographFontFamily: false,
  };
}
